/**
 * Speech Service
 * 
 * Uses MediaRecorder to capture audio and sends it to the external transcript API.
 */

import chatService from './chatService';

// Spec says max 25MB. Cap recordings well below that to leave headroom.
// At ~32kbps opus that is ~100 minutes, but we cap at 5 minutes for UX.
const MAX_RECORDING_MS = 5 * 60 * 1000;

// Preferred MIME types in order of preference. The transcript API spec lists
// wav, mp3, mp4, m4a, flac, etc. — webm/ogg are not explicitly listed, so
// prefer mp4 (Safari) and fall back to whatever the browser supports.
const PREFERRED_MIME_TYPES = [
  'audio/mp4',
  'audio/mpeg',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

const EXTENSION_BY_MIME = {
  'audio/mp4': 'mp4',
  'audio/mpeg': 'mp3',
  'audio/webm': 'webm',
  'audio/ogg': 'ogg',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
};

const pickRecorderMimeType = () => {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return '';
  }
  for (const candidate of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(candidate)) {
      return candidate;
    }
  }
  return '';
};

const extensionFor = (mimeType) => {
  if (!mimeType) return 'webm';
  const base = mimeType.split(';')[0].trim();
  return EXTENSION_BY_MIME[base] || base.split('/')[1] || 'webm';
};

class SpeechService {
  constructor() {
    this.isRecognizing = false;
    this.accumulatedText = '';
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.stream = null;
    this.recorderMimeType = '';
    this.maxDurationTimer = null;
    // Promise that resolves with the final transcript when processAudio finishes.
    this.processingDeferred = null;

    // Callbacks
    this.onInterimCallback = null;
    this.onResultCallback = null;
    this.onErrorCallback = null;

    console.log('🎤 Speech Service initialized');
  }

  isConfigured() {
    return true;
  }

  async requestMicrophonePermission() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.stream = stream;
      return { success: true };
    } catch (error) {
      console.error('🎤 Microphone access denied:', error);
      return { success: false, error };
    }
  }

  releaseMicrophone() {
    if (this.stream) {
      try {
        this.stream.getTracks().forEach(track => track.stop());
      } catch (err) {
        console.warn('⚠️ Error stopping mic tracks:', err);
      }
      this.stream = null;
    }
  }

  async startRecognition(onInterim, onResult, onError) {
    if (this.isRecognizing) {
      return { success: false, error: 'Already recording' };
    }

    if (!chatService.accessToken) {
      console.warn('⚠️ No access token available for Speech API');
      if (onError) onError('No access token available');
      return { success: false, error: 'No access token' };
    }

    this.onInterimCallback = onInterim;
    this.onResultCallback = onResult;
    this.onErrorCallback = onError;

    // Acquire mic permission BEFORE flipping any UI state so the caller can
    // wait on the prompt without showing a misleading "ready to listen" hint.
    const permResult = await this.requestMicrophonePermission();
    if (!permResult.success) {
      if (onError) onError('Microphone access denied');
      this.cleanup();
      return permResult;
    }

    this.audioChunks = [];
    this.clearAccumulatedText();

    // Deferred promise resolved by processAudio() when transcription completes
    // or rejected on error. stopRecognition() returns this so the caller can
    // await the final text.
    this.processingDeferred = (() => {
      let resolve, reject;
      const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
      return { promise, resolve, reject };
    })();

    try {
      this.recorderMimeType = pickRecorderMimeType();
      const recorderOptions = this.recorderMimeType ? { mimeType: this.recorderMimeType } : undefined;
      this.mediaRecorder = new MediaRecorder(this.stream, recorderOptions);

      // If we passed no options, capture whatever the browser actually picked
      // so we can label the Blob/file correctly.
      if (!this.recorderMimeType && this.mediaRecorder.mimeType) {
        this.recorderMimeType = this.mediaRecorder.mimeType;
      }

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = async () => {
        console.log('🛑 Audio recording stopped, processing...');
        this.clearMaxDurationTimer();
        await this.processAudio();
      };

      this.mediaRecorder.start();
      this.isRecognizing = true;

      // Auto-stop if the user records for too long to avoid hitting the
      // 25MB API limit.
      this.maxDurationTimer = setTimeout(() => {
        console.log('⏱️ Max recording duration reached, stopping');
        this.stopRecognition();
      }, MAX_RECORDING_MS);

      console.log('🎤 Recording with MIME type:', this.recorderMimeType || '(browser default)');

      return { success: true };
    } catch (error) {
      console.error('🎤 Error starting MediaRecorder:', error);
      if (onError) onError(error.message || 'Error starting recording');
      this.processingDeferred?.reject(error);
      this.processingDeferred = null;
      this.releaseMicrophone();
      this.cleanup();
      return { success: false, error };
    }
  }

  /**
   * Stop recording and return the eventual transcript.
   * @returns {Promise<string>} resolves with the final transcript text, or '' if none.
   */
  stopRecognition() {
    console.log('🛑 Stopping speech recognition...');
    this.clearMaxDurationTimer();
    const pending = this.processingDeferred?.promise || Promise.resolve('');
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      // onstop -> processAudio() will settle the deferred.
      this.mediaRecorder.stop();
    } else {
      // Nothing to process; settle now.
      this.processingDeferred?.resolve('');
      this.processingDeferred = null;
    }
    this.isRecognizing = false;
    return pending;
  }

  clearMaxDurationTimer() {
    if (this.maxDurationTimer) {
      clearTimeout(this.maxDurationTimer);
      this.maxDurationTimer = null;
    }
  }

  async processAudio() {
    // Snapshot callbacks/deferred before cleanup() nulls them.
    const onResult = this.onResultCallback;
    const onError = this.onErrorCallback;
    const deferred = this.processingDeferred;
    this.processingDeferred = null;

    if (this.audioChunks.length === 0) {
      console.warn('⚠️ No audio data recorded');
      this.releaseMicrophone();
      this.cleanup();
      deferred?.resolve('');
      return;
    }

    const blobType = this.recorderMimeType || 'application/octet-stream';
    const audioBlob = new Blob(this.audioChunks, { type: blobType });
    const filename = `recording.${extensionFor(this.recorderMimeType)}`;

    const formData = new FormData();
    formData.append('file', audioBlob, filename);
    formData.append('session_token', chatService.accessToken);

    try {
      console.log('📤 Sending audio to Transcript API...', {
        size: audioBlob.size,
        type: blobType,
        filename,
      });
      const response = await fetch(`${chatService.apiBaseUrl}/v1/external/audio/transcript`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${chatService.shareToken}`
          // Don't set Content-Type, browser will set it with the boundary automatically for FormData
        },
        body: formData
      });

      if (!response.ok) {
        let detail = '';
        try {
          const err = await response.json();
          detail = err?.error || err?.message || '';
        } catch {
          // ignore parse errors
        }
        throw new Error(`Transcript API failed: ${response.status}${detail ? ` - ${detail}` : ''}`);
      }

      const data = await response.json();
      console.log('✅ Transcript API response:', data);

      const finalText = data?.text || '';
      if (finalText) {
        this.accumulatedText = finalText;
        if (onResult) onResult(finalText);
      } else {
        console.warn('⚠️ No text returned from transcript API');
      }
      deferred?.resolve(finalText);
    } catch (error) {
      console.error('❌ Error calling Transcript API:', error);
      if (onError) onError(error?.message || 'Failed to transcribe audio');
      deferred?.reject(error);
    } finally {
      this.releaseMicrophone();
      this.cleanup();
    }
  }

  cleanup() {
    this.isRecognizing = false;
    this.audioChunks = [];
    this.recorderMimeType = '';
    this.mediaRecorder = null;
    this.clearMaxDurationTimer();
    this.onInterimCallback = null;
    this.onResultCallback = null;
    this.onErrorCallback = null;
  }

  clearAccumulatedText() {
    this.accumulatedText = '';
  }

  getStatus() {
    return {
      isRecognizing: this.isRecognizing,
      isConfigured: this.isConfigured(),
    };
  }
}

// Export singleton instance
export default new SpeechService();
